import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getFacilityMappingList,
  getIntakeFormList,
  updateDefaultIntakeForm,
  updateIntakeFormMapping,
  getIntakeformMetaData,
  getIntakeFormListForddl,
  saveintakeform,
  getIntakeFormbyId,
  deleteIntakeForm,
} from '../services/IntakeFormService';
import type {
  DefaultIntakeForm,
  DefaultIntakeFormResponse,
  UpdateMappingList,
  IntakeMetaData,
  IntakeFormList,
  IntakeFormListddl,
  SaveIntakeFormData,
} from '../services/IntakeFormService';

export interface IntakeFormListQueryPerams {
  userId: string;
  page?: number;
  size?: number;
  sortField?: string;
  sortType?: string;
  searchTerm?: string;
  facilityId?: number[];
}

export const useGetIntakeFormList = ({
  userId,
  page = 0,
  size = 10,
  sortField,
  sortType,
  searchTerm,
  facilityId,
}: IntakeFormListQueryPerams) => {
  const { data, isLoading, isError, error, refetch } = useQuery<IntakeFormList>(
    {
      queryKey: [
        'IntakeFormListResponse',
        userId,
        page,
        size,
        sortField,
        sortType,
        searchTerm,
        facilityId,
      ],
      queryFn: async () => {
        const data = await getIntakeFormList(
          userId,
          page,
          size,
          sortField,
          sortType,
          searchTerm,
          facilityId
        );
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    }
  );

  return {
    IntakeFormListResponse: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetFacilityMappingList = (userId: string) => {
  const queryClient = useQueryClient();

  const fetchFacilityMappingList = async (intakeFormId: number) => {
    const data = await queryClient.fetchQuery({
      queryKey: ['facilityMappingList', userId, intakeFormId],
      queryFn: () => getFacilityMappingList(userId, intakeFormId),
      staleTime: 0,
      gcTime: 0,
    });

    return data;
  };

  return { fetchFacilityMappingList };
};

export const useUpdateDefaultIntakeForm = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    DefaultIntakeFormResponse,
    Error,
    DefaultIntakeForm & { userId: string }
  >({
    mutationFn: async ({ userId, ...payload }) => {
      const data = await updateDefaultIntakeForm(userId, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['IntakeFormListResponse'],
      });
      onSuccess();
    },
  });
};

export const useUpdateIntakeFormMapping = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    DefaultIntakeFormResponse,
    Error,
    UpdateMappingList & { userId: string }
  >({
    mutationFn: async ({ userId, ...payload }) => {
      const data = await updateIntakeFormMapping(userId, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['IntakeFormListResponse'],
      });
      onSuccess();
    },
  });
};

export const useGetIntakeFormMetaData = (userId: string) => {
  const { data, isLoading, isError, error, refetch } = useQuery<IntakeMetaData>(
    {
      queryKey: ['IntakeFormMetaData', userId],
      queryFn: async () => {
        const data = await getIntakeformMetaData(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
    }
  );

  return {
    IntakeFormMetaData: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useGetIntakeFormListForddl = (userId: string) => {
  const { data, isLoading, isError, error, refetch } = useQuery<
    IntakeFormListddl[]
  >({
    queryKey: ['IntakeFormListDdl', userId],
    queryFn: async () => {
      const data = await getIntakeFormListForddl(userId);
      return data;
    },
    staleTime: 0,
    gcTime: 0,
    enabled: false,
  });

  return {
    IntakeFormListDdl: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useSaveIntakeForm = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    SaveIntakeFormData,
    Error,
    SaveIntakeFormData & { userId: string; action: string }
  >({
    mutationFn: async ({ userId, action, ...payload }) => {
      const data = await saveintakeform(userId, action, payload);
      return data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['IntakeFormListResponse'],
      });
      onSuccess();
    },
  });
};

export const useGetIntakeFormById = (userId: string, formId: number) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<SaveIntakeFormData>({
      queryKey: ['IntakeFormById', userId, formId],
      queryFn: async () => {
        const data = await getIntakeFormbyId(userId, formId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: !!formId,
    });

  return {
    IntakeFormById: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useDeleteIntakeForm = (onSuccess: () => void) => {
  const queryClient = useQueryClient();
  return useMutation<
    DefaultIntakeFormResponse,
    Error,
    { userId: string; formId: number }
  >({
    mutationFn: async ({ userId, formId }) => {
      const data = await deleteIntakeForm(userId, formId);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['IntakeFormListResponse'],
      });
      onSuccess();
    },
  });
};
